import { type FC, useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Select from '__components/Inputs/Select';
import Cols from '__components/Layout/Cols';
import { KizenTypography } from '__app/typography';
import { DEFAULT_PATHS } from 'routes/paths';
import { StyledModelMessage, StyledNotice } from './styles';
import { DEVELOPER_DOCS_URL } from './constants';
import type { LLMErrors, ModelOption, ProviderOption } from './types';

import { useFlashTransition } from '__hooks/useFlashState';
import styled from '@emotion/styled';

const Message = styled(StyledModelMessage)`
  a {
    font: inherit;
  }
`;

type LLMProviderSelectProps = {
  value: ModelOption | undefined;
  options: ProviderOption[];
  onChange: (model: ModelOption) => void;
  errors: LLMErrors;
  disabled: boolean;
  modelJustification?: string;
  labelInfo?: string;
};

export const LLMProviderSelect: FC<LLMProviderSelectProps> = ({
  value,
  options,
  onChange,
  errors,
  disabled,
  modelJustification,
  labelInfo,
}) => {
  const { t } = useTranslation();

  const [message, showMessage, flashMessage] = useFlashTransition();

  useEffect(() => {
    if (errors.provider?.hasError) {
      flashMessage(errors.provider.message);
    }
  }, [flashMessage, errors.provider]);

  const validate = useMemo(
    () => ({ message, showMessage }),
    [message, showMessage]
  );

  const notice = useMemo(() => {
    if (value?.model.is_deprecated) {
      const model = options
        .find((opt) => opt.details.id === value.model.business_plugin_app_id)
        ?.options.find((m) => m.value === value.model.suggested_replacement);
      return t(
        'This model is marked as deprecated due to the provider ending support. We recommend using {{recommendedModel}}.',
        {
          recommendedModel:
            model?.label ||
            value.model.suggested_replacement ||
            t('a different model'),
        }
      );
    } else {
      return modelJustification;
    }
  }, [modelJustification, value, options, t]);

  return (
    <>
      <Cols columns={3} gutter="20px">
        <Select
          label={t('Choose Model')}
          labelInfo={
            labelInfo ??
            t(
              'Model choices are determined by both connections set up in your Marketplace as well as file capabilities supported by the model.'
            )
          }
          labelInfoPlacement="top"
          labelInfoMaxWidth={300}
          placeholder={t('Choose Model')}
          value={value}
          options={options}
          onChange={onChange}
          menuInline
          validate={validate}
          error={errors.provider?.hasError}
          disabled={disabled}
          captureMenuScroll={false}
        />
        {notice ? <StyledNotice>{notice}</StyledNotice> : null}
      </Cols>
      <Cols columns={1} gutter="20px">
        <Message>
          <Trans
            i18nKey="Enable more providers in <marketplace>App Marketplace</marketplace> to access more models"
            components={{
              marketplace: (
                <KizenTypography
                  type="anchor"
                  href={DEFAULT_PATHS.MARKETPLACE}
                  data-qa-id="llm-provider-marketplace-link"
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
            }}
          />
        </Message>
        {value?.is_kizen_model ? (
          <Message fontStyle="italic" color="var(--color-font-placeholder)">
            <Trans
              i18nKey="Learn more about how Kizen optimizes model choices in our <docs>Developer Documentation</docs>."
              components={{
                docs: (
                  <KizenTypography
                    type="anchor"
                    href={DEVELOPER_DOCS_URL}
                    data-qa-id="llm-provider-developer-docs-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </Message>
        ) : null}
        {value?.is_billed_via_kizen ? (
          <Message fontStyle="italic" color="var(--color-font-placeholder)">
            {t(
              'Charges for use of external AI models via Kizen will be automatically added to your Kizen plan bill.'
            )}
          </Message>
        ) : null}
      </Cols>
    </>
  );
};
